import React, {useCallback, useState} from 'react';
import {DescriptorFormState, UseDescriptorFormReturn, ValidationState} from './descriptor-types';
import {DescriptorState, MAX_TEXT_LENGTH, URI_REGEX} from './descriptor-constants';
import OccurrenceReportDescriptor from '../../../../../@types/occurrence-report-descriptor';

/**
 * Custom hook for managing descriptor form state and validation
 */
export function useDescriptorForm(initialDescriptor?: OccurrenceReportDescriptor): UseDescriptorFormReturn {
    // Form state
    const [formState, setFormState] = useState<DescriptorFormState>({
        name: initialDescriptor?.name || '',
        description: initialDescriptor?.description || '',
        uri: initialDescriptor?.uri || '',
        status: initialDescriptor?.descriptorState || DescriptorState.DRAFT,
        groupId: initialDescriptor?.group?.id || 0,
        hasBeenPublished: initialDescriptor?.hasBeenPublished || false
    });

    // Validation state
    const [validation, setValidation] = useState<ValidationState>({
        nameError: false,
        descriptionError: false,
        uriError: false
    });

    // Update entire form state
    const updateFormState = useCallback((newState: Partial<DescriptorFormState>) => {
        setFormState(prev => ({ ...prev, ...newState }));
    }, []);

    // Field change handlers
    const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value || '';
        if (value.length <= MAX_TEXT_LENGTH) {
            setFormState(prev => ({ ...prev, name: value }));
        }
        setValidation(prev => ({ 
            ...prev, 
            nameError: value.trim() === '' 
        }));
    }, []);

    const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value || '';
        if (value.length <= MAX_TEXT_LENGTH) {
            setFormState(prev => ({ ...prev, description: value }));
        }
        setValidation(prev => ({ 
            ...prev, 
            descriptionError: value.trim() === '' 
        }));
    }, []);

    const handleUriChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value.trim();
        setFormState(prev => ({ ...prev, uri: value }));
    }, []);

    const handleUriBlur = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value.trim();
        setValidation(prev => ({ 
            ...prev, 
            uriError: value !== '' && !URI_REGEX.test(value) 
        }));
    }, []);

    const handleGroupChange = useCallback((event: any) => {
        setFormState(prev => ({ ...prev, groupId: event.target.value as number }));
    }, []);

    const handleStatusChange = useCallback((newStatus: string) => {
        setFormState(prev => ({ ...prev, status: newStatus }));
    }, []);

    // Validate entire form
    const validateForm = useCallback((): boolean => {
        const newValidation: ValidationState = {
            nameError: !formState.name || formState.name.trim() === '',
            descriptionError: !formState.description || formState.description.trim() === '',
            uriError: formState.uri !== '' && !URI_REGEX.test(formState.uri)
        };
        setValidation(newValidation);
        
        return !newValidation.nameError && 
               !newValidation.descriptionError && 
               !newValidation.uriError;
    }, [formState]);

    // Reset form to initial state
    const resetForm = useCallback((descriptor?: OccurrenceReportDescriptor) => {
        setFormState({
            name: descriptor?.name || '',
            description: descriptor?.description || '',
            uri: descriptor?.uri || '',
            status: descriptor?.descriptorState || DescriptorState.DRAFT,
            groupId: descriptor?.group?.id || 0,
            hasBeenPublished: descriptor?.hasBeenPublished || false
        });
        setValidation({
            nameError: false,
            descriptionError: false,
            uriError: false
        });
    }, []);

    // Load descriptor data into form
    const loadDescriptor = useCallback((descriptor: OccurrenceReportDescriptor, targetGroup?: number) => {
        const groupId = descriptor.group?.id || targetGroup || 0;
        setFormState({
            name: descriptor.name || '',
            description: descriptor.description || '',
            uri: descriptor.uri || '',
            status: descriptor.descriptorState || DescriptorState.DRAFT,
            groupId: groupId,
            hasBeenPublished: descriptor.hasBeenPublished || false
        });
    }, []);

    return {
        formState,
        validation,
        handleNameChange,
        handleDescriptionChange,
        handleUriChange,
        handleUriBlur,
        handleGroupChange,
        handleStatusChange,
        validateForm,
        resetForm,
        loadDescriptor,
        updateFormState
    };
}